import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { FriendDto, GameSummaryDto, RoomDto } from '@cardhub/protocol';
import { api, apiUrl } from '../api/client';
import { Avatar, Coins, Empty, ErrorState, Icon, Loading, Modal, TopBar } from '../components/ui';
import { useT } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';
import { copyText, shareOrCopy } from '../lib/device';
import { useGame } from '../store/game';
import { realtime, useSession } from '../store/session';
import { toast } from '../store/ui';

export function RoomCreate() {
  const t = useT();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const games = useAsync(() => api.get<GameSummaryDto[]>('/api/v1/games'), []);
  const [game, setGame] = useState(search.get('game') ?? 'CALL_BREAK');
  const [configId, setConfigId] = useState('');
  const [password, setPassword] = useState('');
  const bots = true;
  const [busy, setBusy] = useState(false);
  const g = games.data?.find((x) => x.type === game);
  useEffect(() => {
    if (g && !g.tables.some((c) => c.id === configId)) setConfigId(g.tables[0]?.id ?? '');
  }, [g, configId]);

  const create = async () => {
    setBusy(true);
    try {
      const room = await api.post<RoomDto>('/api/v1/rooms', { tableConfigId: configId, fillWithBots: bots, ...(password ? { password } : {}) });
      nav(`/rooms/${room.code}`, { replace: true });
    } catch (e) {
      toast(errorMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page">
      <TopBar title={t('rooms.create')} onBack={() => nav(-1)} />
      {games.loading && <Loading />}
      {games.error && <ErrorState message={games.error} onRetry={games.reload} />}
      {games.data && (
        <div className="col">
          <div className="field">
            <label htmlFor="rg">{t('rooms.game')}</label>
            <select id="rg" className="input" value={game} onChange={(e) => setGame(e.target.value)}>
              {games.data.filter((x) => x.enabled).map((x) => (
                <option key={x.type} value={x.type}>{t(`game.${x.type}`)}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="rt">{t('rooms.table')}</label>
            <select id="rt" className="input" value={configId} onChange={(e) => setConfigId(e.target.value)}>
              {g?.tables.map((c) => (
                <option key={c.id} value={c.id}>{c.name} — {new Intl.NumberFormat().format(c.entryFee)}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="rp">{t('rooms.password')}</label>
            <input id="rp" className="input" type="text" minLength={4} maxLength={32} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
          </div>
          <button className="btn primary lg block" disabled={!configId || busy || (password.length > 0 && password.length < 4)} onClick={() => void create()}>
            {t('rooms.create')}
          </button>
        </div>
      )}
    </main>
  );
}

export function RoomLobby() {
  const { code = '' } = useParams();
  const t = useT();
  const nav = useNavigate();
  const me = useSession((s) => s.user)!;
  const [room, setRoom] = useState<(RoomDto & { isMember?: boolean }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const friends = useAsync(() => (inviteOpen ? api.get<FriendDto[]>('/api/v1/friends') : Promise.resolve([])), [inviteOpen]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  const load = async () => {
    try {
      setRoom(await api.get<RoomDto & { isMember: boolean }>(`/api/v1/rooms/code/${code}`));
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => {
    void load();
    const off = realtime.on('room.update.v1', (d) => {
      const r = d as RoomDto & { removed?: boolean; roomId?: string };
      if (r.removed) {
        toast('You were removed from the room', 'error');
        nav('/');
        return;
      }
      if (r.code === code) {
        setRoom((cur) => ({ ...r, isMember: cur?.isMember ?? true }));
        if (r.status === 'IN_GAME' && r.tableId) nav(`/table/${r.tableId}`);
        if (r.status === 'CANCELLED') {
          toast('The host closed the room', 'info');
          nav('/');
        }
      }
    });
    return () => void off();
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const join = async () => {
    try {
      setRoom({ ...(await api.post<RoomDto>(`/api/v1/rooms/code/${code}/join`, password ? { password } : {})), isMember: true });
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  const owner = room?.ownerId === me.id;
  const share = async () => {
    if (!room) return;
    const text = t('rooms.shareText', { game: t(`game.${room.gameType}`), code: room.code, url: room.inviteUrl });
    const r = await shareOrCopy({ title: 'Card Hub', text, url: room.inviteUrl });
    if (r === 'copied') toast(t('common.copied'), 'success');
  };
  const call = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  if (error) return <main className="page"><TopBar title={t('rooms.join')} onBack={() => nav('/')} /><ErrorState message={error} onRetry={() => void load()} /></main>;
  if (!room) return <main className="page"><Loading /></main>;
  const isMember = room.isMember ?? room.members.some((m) => m.user.id === me.id);

  return (
    <main className="page">
      <TopBar title={t(`game.${room.gameType}`)} onBack={() => nav('/')} />
      <section className="card pad-lg col" style={{ alignItems: 'center', textAlign: 'center' }}>
        <span className="muted">{t('rooms.code')}</span>
        <strong className="num" style={{ fontSize: 'var(--fs-3xl)', letterSpacing: '0.25em' }}>{room.code}</strong>
        <span className="dim">{room.tableName} · <Coins amount={room.entryFee} size={16} /></span>
        <img src={apiUrl(`/api/v1/rooms/code/${room.code}/qr.svg`)} alt={`QR code to join room ${room.code}`} width={160} height={160} style={{ background: '#fff', borderRadius: 12, padding: 6 }} />
        <div className="row wrap" style={{ justifyContent: 'center' }}>
          <button className="btn sm" onClick={() => void copyText(room.inviteUrl).then((ok) => ok && toast(t('common.copied'), 'success'))}><Icon name="copy" size={16} /> {t('common.copy')}</button>
          <button className="btn sm" onClick={() => void share()}><Icon name="share" size={16} /> {t('common.share')}</button>
          <a className="btn sm" href={`https://wa.me/?text=${encodeURIComponent(t('rooms.shareText', { game: t(`game.${room.gameType}`), code: room.code, url: room.inviteUrl }))}`} target="_blank" rel="noreferrer">WhatsApp</a>
          <a className="btn sm" href={`sms:?&body=${encodeURIComponent(room.inviteUrl)}`}>SMS</a>
          <a className="btn sm" href={`mailto:?subject=Card%20Hub&body=${encodeURIComponent(room.inviteUrl)}`}>Email</a>
          {isMember && <button className="btn sm primary" onClick={() => setInviteOpen(true)}>{t('rooms.invite')}</button>}
        </div>
      </section>

      <h2 className="section-title">{t('rooms.members', { n: room.members.length, max: room.maxPlayers })}</h2>
      <div className="list">
        {room.members.map((m) => (
          <div key={m.user.id} className="list-item">
            <Avatar avatar={m.user.avatar} size={40} />
            <span className="grow">{m.user.username}{m.user.id === room.ownerId && <span className="pill gold" style={{ marginLeft: 8 }}>Host</span>}</span>
            {owner && m.user.id !== me.id && <button className="btn sm ghost" onClick={() => void call(() => api.del(`/api/v1/rooms/${room.id}/members/${m.user.id}`))}>{t('rooms.remove')}</button>}
          </div>
        ))}
        {Array.from({ length: Math.max(0, room.maxPlayers - room.members.length) }, (_, i) => (
          <div key={i} className="list-item muted">
            <span className="avatar" style={{ ['--size' as string]: '40px', background: 'var(--surface-2)', borderStyle: 'dashed' }} />
            Waiting…
          </div>
        ))}
      </div>

      <div className="col" style={{ marginTop: 16 }}>
        {!isMember && (
          <>
            {room.hasPassword && <input className="input" placeholder={t('auth.password')} value={password} onChange={(e) => setPassword(e.target.value)} aria-label={t('auth.password')} />}
            <button className="btn primary lg block" onClick={() => void join()}>{t('rooms.join')}</button>
          </>
        )}
        {isMember && owner && (
          <>
            <button className="btn primary lg block" disabled={room.status !== 'OPEN'} onClick={() => void call(async () => {
              const r = await api.post<{ tableId: string }>(`/api/v1/rooms/${room.id}/start`);
              useGame.setState({ mm: { state: 'MATCHED', tableConfigId: room.tableConfigId, elapsedMs: 0, timeoutMs: 0, playersFound: 0, playersNeeded: 0, tableId: r.tableId } });
              nav(`/table/${r.tableId}`);
            })}>{t('rooms.start')}</button>
            <div className="row">
              <button className="btn ghost grow" onClick={() => void call(() => api.patch(`/api/v1/rooms/${room.id}`, { locked: !room.locked }))}><Icon name={room.locked ? 'unlock' : 'lock'} size={16} /> {room.locked ? t('rooms.unlock') : t('rooms.lock')}</button>
              <button className="btn ghost grow" style={{ color: 'var(--danger)' }} onClick={() => void call(async () => { await api.del(`/api/v1/rooms/${room.id}`); nav('/'); })}>{t('rooms.cancel')}</button>
            </div>
          </>
        )}
        {isMember && !owner && (
          <>
            <p className="dim" style={{ textAlign: 'center' }}>{room.status === 'IN_GAME' ? '' : t('rooms.waiting')}</p>
            {room.status === 'IN_GAME' && room.tableId && <button className="btn primary block" onClick={() => nav(`/table/${room.tableId}`)}>{t('lobby.rejoin')}</button>}
            <button className="btn ghost block" onClick={() => void call(async () => { await api.post(`/api/v1/rooms/${room.id}/leave`); nav('/'); })}>{t('table.leave')}</button>
          </>
        )}
      </div>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title={t('rooms.invite')}>
        {friends.loading ? <Loading /> : friends.data?.length === 0 ? <Empty emoji="🤝" title={t('friends.empty')} /> : (
          <div className="list" style={{ maxHeight: 320, overflow: 'auto' }}>
            {friends.data?.map((f) => (
              <label key={f.user.id} className="list-item" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={chosen.has(f.user.id)} onChange={(e) => { const n = new Set(chosen); if (e.target.checked) n.add(f.user.id); else n.delete(f.user.id); setChosen(n); }} />
                <Avatar avatar={f.user.avatar} size={36} status={f.status} />
                <span className="grow">{f.user.username}</span>
              </label>
            ))}
          </div>
        )}
        <button className="btn primary block" style={{ marginTop: 12 }} disabled={chosen.size === 0} onClick={() => void call(async () => {
          const r = await api.post<{ invited: number }>('/api/v1/invites', { roomId: room.id, userIds: [...chosen] });
          toast(`Invited ${r.invited}`, 'success');
          setInviteOpen(false);
          setChosen(new Set());
        })}>{t('rooms.invite')}</button>
      </Modal>
    </main>
  );
}

/** Deep-link landing: /join/:code → (sign in/up) → room lobby. */
export function JoinLanding() {
  const { code = '' } = useParams();
  const nav = useNavigate();
  const status = useSession((s) => s.status);
  useEffect(() => {
    if (status === 'authed') nav(`/rooms/${code.toUpperCase()}`, { replace: true });
    else if (status === 'anon') nav(`/welcome?next=${encodeURIComponent(`/rooms/${code.toUpperCase()}`)}`, { replace: true });
  }, [status, code, nav]);
  return <main className="page"><Loading /></main>;
}
